import { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Text,
  Stack,
  Group,
  Button,
  Badge,
  Divider,
  Select,
  ThemeIcon,
  Modal,
  ActionIcon,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { Check, Link2, ExternalLink, Edit2, Settings, X } from 'react-feather';
import { coachPortalApi } from '../../../services/api/coachPortal';
import { CalendlyBookingHealthAlerts } from '../../../components/coaches/CalendlyBookingHealthAlerts';
import { CalendlyDurationList } from '../../../components/coaches/CalendlyDurationList';
import { MissingMeetingLinkAlerts } from '../../../components/coaches/MissingMeetingLinkAlerts';
import type { MissingMeetingLink } from '../../../types';
import {
  eventTypeOptionLabel,
  eventTypesForLength,
  isLimitedPlan,
  limitedPlanNote,
  twentyMinutePrompt,
  type MappedLength,
} from '../../../components/coaches/calendlyDurations';
import type {
  CalendlyConnectionStatus,
  CalendlyEventTypeOption,
} from '../../../services/api/coachPortal';

interface CalendlyConnectionCardProps {
  onStatusChange?: () => void;
  /** MED-1365 QA round 6: opens the profile field for a missing meeting link. */
  onAddMeetingLink?: (platform: MissingMeetingLink['platform']) => void;
  /** MED-1365 QA round 6: a new value reads the status again, for example after a profile save. */
  refreshToken?: number;
  /** MED-1365 QA round 9: opens the event type mapping window once the status loads. */
  openMappingRequest?: boolean;
  /** MED-1365 QA round 9: called when the card takes the mapping request. */
  onMappingRequestHandled?: () => void;
}

export function CalendlyConnectionCard({
  onStatusChange,
  onAddMeetingLink,
  refreshToken = 0,
  openMappingRequest = false,
  onMappingRequestHandled,
}: CalendlyConnectionCardProps) {
  const [status, setStatus] = useState<CalendlyConnectionStatus | null>(null);
  const [eventTypes, setEventTypes] = useState<CalendlyEventTypeOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [savingMappings, setSavingMappings] = useState(false);
  const [confirmOpened, { open: openConfirm, close: closeConfirm }] = useDisclosure(false);
  const [mappingOpened, { open: openMapping, close: closeMapping }] = useDisclosure(false);
  const [mapping20min, setMapping20min] = useState<string | null>(null);
  const [mapping45min, setMapping45min] = useState<string | null>(null);
  const [mapping60min, setMapping60min] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      const data = await coachPortalApi.getCalendlyStatus();
      setStatus(data);
      setMapping20min(data.event_type_20min_uri);
      setMapping45min(data.event_type_45min_uri);
      setMapping60min(data.event_type_60min_uri);
    } catch {
      // Not connected yet — that's fine
      setStatus({
        connected: false,
        connected_at: null,
        event_types_configured: false,
        event_type_20min_uri: null,
        event_type_45min_uri: null,
        event_type_60min_uri: null,
        unmapped_durations: [20, 45, 60],
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const handleOAuthCallback = useCallback(
    async (code: string, state: string) => {
      setConnecting(true);
      try {
        await coachPortalApi.calendlyCallback(code, state);
        await fetchStatus();
        notifications.show({
          title: 'Calendly Connected',
          message: 'Your Calendly account has been connected successfully.',
          color: 'green',
        });
        onStatusChange?.();
      } catch (err) {
        notifications.show({
          title: 'Connection Failed',
          message: err instanceof Error ? err.message : 'Failed to connect Calendly',
          color: 'red',
        });
        setLoading(false);
      } finally {
        setConnecting(false);
      }
    },
    [fetchStatus, onStatusChange],
  );

  // MED-1365 QA round 6: a saved meeting link removes its note.
  useEffect(() => {
    if (refreshToken > 0) fetchStatus();
  }, [refreshToken, fetchStatus]);

  // Check for OAuth callback params on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const calendlyParam = params.get('calendly');
    const code = params.get('code');
    const state = params.get('state');

    if (calendlyParam === 'callback' && code && state) {
      handleOAuthCallback(code, state);
      // Clean up URL params
      const url = new URL(window.location.href);
      url.searchParams.delete('calendly');
      url.searchParams.delete('code');
      url.searchParams.delete('state');
      window.history.replaceState({}, '', url.toString());
    } else {
      fetchStatus();
    }
  }, [fetchStatus, handleOAuthCallback]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const { authorization_url } = await coachPortalApi.connectCalendly();
      window.location.href = authorization_url;
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to initiate connection',
        color: 'red',
      });
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await coachPortalApi.disconnectCalendly();
      setStatus({
        connected: false,
        connected_at: null,
        event_types_configured: false,
        event_type_20min_uri: null,
        event_type_45min_uri: null,
        event_type_60min_uri: null,
        unmapped_durations: [20, 45, 60],
      });
      setEventTypes([]);
      closeConfirm();
      notifications.show({
        title: 'Disconnected',
        message: 'Calendly has been disconnected from your profile.',
        color: 'gray',
      });
      onStatusChange?.();
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to disconnect',
        color: 'red',
      });
    } finally {
      setDisconnecting(false);
    }
  };

  const handleOpenMappings = async () => {
    try {
      const types = await coachPortalApi.getCalendlyEventTypes();
      setEventTypes(types);
      // MED-1365 QA round 6: a mapping must match its length, and a free or
      // trial plan maps only 20 min. Medley does not use any other mapping,
      // so the window does not show it, and a save clears it.
      const matching = (uri: string | null, length: MappedLength) => {
        const mapped = uri ? types.find((et) => et.uri === uri) : undefined;
        return mapped && mapped.duration !== length ? null : uri;
      };
      const limited = isLimitedPlan(status?.booking_health?.plan_stage);
      setMapping20min(matching(status?.event_type_20min_uri ?? null, 20));
      setMapping45min(limited ? null : matching(status?.event_type_45min_uri ?? null, 45));
      setMapping60min(limited ? null : matching(status?.event_type_60min_uri ?? null, 60));
      openMapping();
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to load event types',
        color: 'red',
      });
    }
  };

  // MED-1365 QA round 9: the dashboard item links to the profile with
  // ?map_calendly=1. The request waits for the status, so the window opens
  // whether the card mounts before or after its status loads. It uses the
  // same open path as the Edit Event Type Mappings button.
  useEffect(() => {
    if (!openMappingRequest || loading || !status) return;
    onMappingRequestHandled?.();
    if (status.connected) void handleOpenMappings();
  });

  const handleSaveMappings = async () => {
    setSavingMappings(true);
    try {
      // QA round 6: a free or trial plan saves 20 min only.
      const limited = isLimitedPlan(status?.booking_health?.plan_stage);
      await coachPortalApi.updateCalendlyMappings({
        calendly_event_type_uri_20min: mapping20min,
        calendly_event_type_uri_45min: limited ? null : mapping45min,
        calendly_event_type_uri_60min: limited ? null : mapping60min,
      });
      await fetchStatus();
      closeMapping();
      notifications.show({
        title: 'Saved',
        message: 'Event type mappings updated.',
        color: 'green',
      });
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to save mappings',
        color: 'red',
      });
    } finally {
      setSavingMappings(false);
    }
  };

  if (loading) return null;

  const connectedDate = status?.connected_at
    ? new Date(status.connected_at).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  // MED-1365 (QA round 2): an event type that is off still shows, so a
  // mapped one is not an empty field, and a save does not clear it.
  // QA round 6: each length lists only the event types of its duration.
  const optionsFor = (length: MappedLength, mapped: string | null) => {
    const options = eventTypesForLength(eventTypes, length).map((et) => ({
      value: et.uri,
      label: eventTypeOptionLabel(et),
    }));
    // A mapped event type that Calendly no longer lists keeps its value too.
    if (mapped && !eventTypes.some((et) => et.uri === mapped)) {
      options.push({ value: mapped, label: 'Mapped event type (not found in Calendly)' });
    }
    return options;
  };
  const planStage = status?.booking_health?.plan_stage ?? null;
  const limitedPlan = isLimitedPlan(planStage);
  const hasActive20 = eventTypes.some((et) => et.duration === 20 && et.active !== false);
  // QA round 7: the prompt names a 20 min event type that is off.
  const offTwentyNames = eventTypes
    .filter((et) => et.duration === 20 && et.active === false)
    .map((et) => et.name);

  // MED-1365 (QA round 2): how each length books, from Calendly's own state.
  const durations = status?.booking_health?.durations ?? null;

  return (
    <>
      <Card withBorder padding="lg" radius="md">
        <Group justify="space-between" mb="sm">
          <Text size="xs" fw={600} c="dimmed" tt="uppercase">
            Connections
          </Text>
          {status?.connected && (
            <ActionIcon variant="subtle" size="sm" onClick={openConfirm} aria-label="Edit connection">
              <Edit2 size={14} />
            </ActionIcon>
          )}
        </Group>

        {status?.connected ? (
          <Stack gap="md">
            <Group gap="sm">
              <ThemeIcon size={28} radius="md" variant="light" color="green">
                <Check size={14} />
              </ThemeIcon>
              <div>
                <Text size="sm" fw={600}>
                  Calendly Connected
                </Text>
                <Text size="xs" c="dimmed">
                  Connected {connectedDate}
                </Text>
              </div>
            </Group>

            {/* MED-1363: Calendly settings that stop participant bookings */}
            <CalendlyBookingHealthAlerts
              health={status.booking_health}
              audience="coach"
            />

            {/* MED-1365 QA round 6: meeting links that the coach's cohorts need */}
            <MissingMeetingLinkAlerts
              links={status.missing_meeting_links}
              audience="coach"
              onAdd={onAddMeetingLink}
            />

            <Divider />

            <div>
              <Text size="xs" c="dimmed" mb={4}>
                Event Type Mappings
              </Text>
              {durations ? (
                <CalendlyDurationList
                  durations={durations}
                  planStage={status.booking_health?.plan_stage ?? null}
                  audience="coach"
                  offTwentyNames={status.booking_health?.off_20min_event_types}
                />
              ) : status.event_types_configured && status.unmapped_durations.length === 0 ? (
                <Badge variant="light" color="green" size="sm">
                  All Configured
                </Badge>
              ) : status.event_types_configured ? (
                <Badge variant="light" color="orange" size="sm">
                  {status.unmapped_durations.length} unmapped
                </Badge>
              ) : (
                <Badge variant="light" color="orange" size="sm">
                  Not configured
                </Badge>
              )}
            </div>

            {!durations && status.unmapped_durations.length > 0 && status.event_types_configured && (
              <Text size="xs" c="orange">
                Missing mappings for: {status.unmapped_durations.map(d => `${d} min`).join(', ')}
              </Text>
            )}

            <Button
              variant="light"
              size="xs"
              leftSection={<Settings size={14} />}
              onClick={handleOpenMappings}
            >
              {status.event_types_configured ? 'Edit Event Type Mappings' : 'Configure Event Types'}
            </Button>
          </Stack>
        ) : (
          <Stack gap="md" align="center" py="sm">
            {/* MED-1365 QA round 6: also before Calendly is connected */}
            <MissingMeetingLinkAlerts
              links={status?.missing_meeting_links}
              audience="coach"
              onAdd={onAddMeetingLink}
            />
            <ThemeIcon size={40} radius="xl" variant="light" color="gray">
              <Link2 size={20} />
            </ThemeIcon>
            <Text size="sm" c="dimmed" ta="center">
              Connect your Calendly account to enable participant scheduling.
            </Text>
            <Button
              variant="filled"
              color="medleyGreen"
              leftSection={<ExternalLink size={14} />}
              loading={connecting}
              onClick={handleConnect}
            >
              Connect Calendly
            </Button>
          </Stack>
        )}
      </Card>

      {/* Disconnect Confirmation Modal */}
      <Modal
        opened={confirmOpened}
        onClose={closeConfirm}
        title="Disconnect Calendly"
        centered
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm">
            Are you sure you want to disconnect your Calendly account? Participant scheduling will
            be disabled until you reconnect.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={closeConfirm}>
              Cancel
            </Button>
            <Button
              color="red"
              leftSection={<X size={14} />}
              loading={disconnecting}
              onClick={handleDisconnect}
            >
              Disconnect
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Event Type Mapping Modal */}
      <Modal
        opened={mappingOpened}
        onClose={closeMapping}
        title="Map Calendly Event Types"
        centered
        size="md"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Map each session length to a Calendly event type of the same length. A length with no
            event type, or one that is off in Calendly, still books online with a Google Calendar
            invite from the Medley calendar.
          </Text>

          {limitedPlan && (
            <Text size="sm" c="dimmed">
              {limitedPlanNote('coach')}
            </Text>
          )}

          <Select
            label="20-Minute Sessions"
            description="Used for coach check-ins"
            placeholder="Select event type..."
            nothingFoundMessage="No 20 min event type in Calendly"
            data={optionsFor(20, mapping20min)}
            value={mapping20min}
            onChange={setMapping20min}
            clearable
          />

          {limitedPlan && !hasActive20 && (
            <Text size="sm" fw={600}>
              {twentyMinutePrompt(planStage, 'coach', offTwentyNames)}
            </Text>
          )}

          {!limitedPlan && (
            <>
              <Select
                label="45-Minute Sessions"
                description="Used for 45-minute 1:1 coaching sessions"
                placeholder="Select event type..."
                nothingFoundMessage="No 45 min event type in Calendly"
                data={optionsFor(45, mapping45min)}
                value={mapping45min}
                onChange={setMapping45min}
                clearable
              />

              <Select
                label="60-Minute Sessions"
                description="Used for 60-minute 1:1 coaching sessions"
                placeholder="Select event type..."
                nothingFoundMessage="No 60 min event type in Calendly"
                data={optionsFor(60, mapping60min)}
                value={mapping60min}
                onChange={setMapping60min}
                clearable
              />
            </>
          )}

          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={closeMapping}>
              Cancel
            </Button>
            <Button
              color="medleyGreen"
              loading={savingMappings}
              onClick={handleSaveMappings}
            >
              Save Mappings
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
